import * as Sentry from '@sentry/vue';
import { initializeApp } from 'firebase/app';
import { getAuth, onIdTokenChanged } from 'firebase/auth';
import '@mdi/font/css/materialdesignicons.css';
import { createApp, markRaw } from 'vue';
import 'vuetify/styles';
import { createVuetify } from 'vuetify';
import { aliases, mdi } from 'vuetify/iconsets/mdi';
import { v1 as uuidv1 } from 'uuid';

import { EventLog } from '@/lib/event-log';
import { INTERNAL_IDENTITY_METHOD } from '@/lib/identity-methods';
import theme from './lib/color-theme';
import './sass/icons.scss'; // Icons are loaded separately due to relative namespacing.
import './sass/main.scss';
import App from './App.vue';
import router from './router/index';
import store from './store/index';
import Services from './services/index';
import './directives/focus';

import './lib/remove-polyfill';
import './lib/url-polyfill';

let app;
let appMounted = false;

// Clears the static loader painted by index.html. Called once Vue has mounted and
// can render its own loading states, and again if boot fails — a spinner left over
// a permanently blank page reads as a hang rather than an error.
const removeBootLoader = () => {
  document.getElementById('app-boot-loader')?.remove();
};

const queryParams = new URLSearchParams(window.location.search);
const TRACK_ENABLED = queryParams.has('track');
const DEBUG_ENABLED = VUE_DEBUG_ENABLED || TRACK_ENABLED;

const vuetify = createVuetify({
  theme: theme.theme,
  icons: {
    defaultSet: 'mdi',
    aliases,
    sets: {
      mdi,
    },
  },
});

const initializeVue = (message) => {
  if (!app) {
    app = createApp(App);

    if (DEBUG_ENABLED) {
      /* eslint-disable no-console */
      console.info('debug: enabled (t=%s, v=%s)', TRACK_ENABLED, VUE_DEBUG_ENABLED);
      /* eslint-enable no-console */
      app.config.devtools = true;
      app.config.debug = true;
      app.config.silent = false;
    } else {
      /* eslint-disable no-console */
      console.info('debug: disabled (t=%s, v=%s)', TRACK_ENABLED, VUE_DEBUG_ENABLED);
      /* eslint-enable no-console */
      app.config.devtools = false;
      app.config.debug = false;
      app.config.silent = true;
    }

    app.use(vuetify);
    app.use(Services);
    app.use(store);

    store.commit('SET_APP', markRaw(app));
    Object.defineProperty(app.config.globalProperties, '$notify', {
      get() {
        return (msg) => {
          app.config.globalProperties.$store.dispatch('createSnack', msg);
        };
      },
    });
    // app.mount('#app');
    if (DEBUG_ENABLED) {
      window.App = app;

      /* eslint no-console: ["error", { allow: ["error"] }] */
      console.error('cf. window.App');
    }
  }
  if (message) {
    app.config.globalProperties.$notify(message);
  }

  // Set session ID to track user's current app session
  sessionStorage.setItem('track_id', uuidv1());

  Services.config
    .getConfig()
    .then((config) => {
      if (config.sentry) {
        Sentry.init({
          app,
          dsn: config.sentry.dsn,
          environment: config.sentry.environment,
          integrations: [Sentry.browserTracingIntegration({ router })],
        });
        Sentry.setTag('track_id', sessionStorage.getItem('track_id'));
      }

      store.dispatch('setConfig', config);
      // Initialize Google Identity Platform
      const firebaseApp = initializeApp(config.identity);
      Object.defineProperty(app.config.globalProperties, '$firebaseApp', {
        get() {
          return firebaseApp;
        },
      });
      const auth = getAuth(app.config.globalProperties.$firebaseApp);
      Object.defineProperty(app.config.globalProperties, '$firebaseAuth', {
        get() {
          return auth;
        },
      });

      // Router required the firbaseAuth to be configured
      // So this is placed below that configuration otherwise
      // it will thrown an error
      // Check if this has been ran already
      // If not SSO it will attempt to run a second time and give
      // a blank page
      auth.onAuthStateChanged(() => {
        if (!appMounted) {
          app.use(router);
          // Mount on settle, not on success. isReady() rejects when the initial
          // navigation is aborted — a guard that throws, or one that redirects
          // into a route whose lazy chunk fails to load. On a `.then()`-only
          // chain that rejection is silent: the app never mounts, the static
          // #app-boot-loader from index.html is never removed, and the page
          // spins forever with nothing in the console but an unhandled
          // rejection. Mounting anyway is always better — the router lands on
          // its fallback route and the user gets a real view (usually /signin)
          // instead of a dead spinner.
          const mountApp = () => {
            if (appMounted) return;
            appMounted = true;
            app.mount('#app');
            removeBootLoader();
          };
          router.isReady().then(mountApp, (error) => {
            console.error('initial navigation failed:', error);
            Sentry.withScope((scope) => {
              scope.setContext('boot', { action: 'router.isReady' });
              Sentry.captureException(error);
            });
            mountApp();
          });

          // isReady() can also never settle at all: a guard that returns without
          // calling next() leaves the initial navigation pending forever, and a
          // promise that never settles fires neither handler above. That is the
          // difference between a slow boot and a hung one, and only a timer can
          // tell them apart. Mount anyway once the wait stops being plausible —
          // mountApp is idempotent, so a genuinely slow navigation that lands
          // afterwards is a no-op rather than a double mount.
          setTimeout(() => {
            if (appMounted) return;
            const error = new Error('initial navigation did not settle within 10s; mounting anyway');
            console.error(error.message);
            Sentry.withScope((scope) => {
              scope.setContext('boot', { action: 'router.isReady', timedOut: true });
              Sentry.captureException(error);
            });
            mountApp();
          }, 10000);
        }
      });

      onIdTokenChanged(auth, (userInfo) => {
        if (userInfo) {
          userInfo.getIdToken().then((idToken) => {
            if (store.getters.configAuth === 'authv2') {
              if (!store.getters.user && store.getters.loginComplete) {
                store.dispatch('setLoginComplete', false);
                const ssoUser =
                  userInfo.providerData &&
                  userInfo.providerData.length > 0 &&
                  userInfo.providerData[0].providerId !== INTERNAL_IDENTITY_METHOD;
                if (ssoUser) {
                  let accountMatch = true;
                  const identityEmail = userInfo.email;
                  const signinEmail = localStorage.getItem('signinEmail');
                  if (signinEmail) {
                    accountMatch = identityEmail && signinEmail.toLowerCase() === identityEmail.toLowerCase();
                    if (!accountMatch) {
                      store.dispatch('logout').then(() => {
                        app.config.globalProperties.$notify(
                          'Account does not match the email address initially provided.'
                        );
                        const failLog = new EventLog({
                          event: 'account.fail_login',
                          email: signinEmail,
                          error: 'account_mismatch',
                        });
                        Services.users.postTrackingLog(failLog);
                      });
                    }
                  }
                  if (!signinEmail || accountMatch) {
                    // Always exchange. This used to be skipped on a URL that already
                    // carried a tenant (`/t/<id>/...`), on the reasoning that the
                    // exchange only exists to discover the default tenant and redirect
                    // there. But `setLoginComplete(false)` has already run above, and
                    // exchangeToken's `.finally()` is the only thing that sets it back
                    // to true — so skipping it stranded the flag at false forever, and
                    // LoginPortal renders a skeleton loader instead of the email form
                    // whenever `loginComplete` is false. That is the "stuck loading
                    // forever" signin page, with nothing in the console to explain it.
                    //
                    // exchangeToken already branches on the same `/t/` check itself and
                    // does the right thing for both URL shapes, so there is nothing to
                    // guard against here.
                    store.dispatch('exchangeToken', idToken).catch((error) => {
                      store.dispatch('logout').then(() => {
                        app.config.globalProperties.$notify('Login failed.');
                        const failLog = new EventLog({
                          event: 'account.fail_login',
                          email: userInfo.email,
                          error: error ? error.message : 'Login failed no error message',
                        });
                        Services.users.postTrackingLog(failLog);
                      });
                    });
                  }
                } else {
                  store.dispatch('setLoginComplete', true);
                }
              }
            } else {
              store.dispatch('setLoginComplete', false);
              if (store.getters.configAuth !== 'authv1') {
                // eslint-disable-next-line no-console
                console.log('authv1 route but not authv1');
                Sentry.withScope((scope) => {
                  Sentry.setContext('on_id_token_changed', {
                    action: 'auth version checking',
                    authVersion: store.getters.configAuth,
                  });
                  Sentry.captureException(new Error('Fell through to authv1 but configAuth does not equal authv1'));
                });
              }
              store.dispatch('setAccessToken', idToken);
              if (!localStorage.getItem('claimsToken') && !store.getters.user) {
                const ssoUser =
                  userInfo.providerData &&
                  userInfo.providerData.length > 0 &&
                  userInfo.providerData[0].providerId !== INTERNAL_IDENTITY_METHOD;
                if (ssoUser) {
                  let accountMatch = true;
                  const identityEmail = userInfo.email;
                  const signinEmail = localStorage.getItem('signinEmail');
                  if (signinEmail) {
                    accountMatch = identityEmail && signinEmail.toLowerCase() === identityEmail.toLowerCase();
                    if (!accountMatch) {
                      store.dispatch('logout').then(() => {
                        app.config.globalProperties.$notify(
                          'Account does not match the email address initially provided.'
                        );
                        const failLog = new EventLog({
                          event: 'account.fail_login',
                          email: signinEmail,
                          error: 'account_mismatch',
                        });
                        Services.users.postTrackingLog(failLog);
                      });
                    }
                  }
                  if (!signinEmail || accountMatch) {
                    store.dispatch('login').catch((error) => {
                      store.dispatch('logout').then(() => {
                        app.config.globalProperties.$notify('Login failed.');
                        const failLog = new EventLog({
                          event: 'account.fail_login',
                          email: userInfo.email,
                          error: error.message,
                        });
                        Services.users.postTrackingLog(failLog);
                      });
                    });
                  }
                } else {
                  store.dispatch('setLoginComplete', true);
                }
              } else if (!store.getters.user) {
                store.dispatch('setLoginComplete', true);
              }
            }
          });
        } else {
          store.dispatch('setLoginComplete', true);
        }
      });
    })
    .catch((error) => {
      console.error('getConfig failed:', error);
      // Boot cannot proceed, so the app will never mount. Drop the loader rather
      // than spinning indefinitely over a page that is not coming.
      removeBootLoader();
    });
};

initializeVue();
